import React, { useReducer, useEffect, createElement, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import PublicContext from "../contexts/PublicContext.jsx";
import { showErrorToast } from "../services/toastService.js";
import ConfirmModal from "../components/modals/ConfirmModal.jsx";
import { useAuth } from "../contexts/AuthContextProvider.jsx";

const SET_ACTIVE_TAB = "SET_ACTIVE_TAB";
const SET_DATA = "SET_DATA";
const SET_ALL_DATA = "SET_ALL_DATA";
const TOGGLE_MENU = "TOGGLE_MENU";
const CLOSE_MENU = "CLOSE_MENU";
const SET_DIRTY = "SET_DIRTY";

const UPDATE_TIME = 1000 * 60 * 5;

const settingsReducer = (state, action) => {
	switch (action.type) {
		case SET_ACTIVE_TAB:
			return { ...state, activeTab: action.payload };
		case SET_DATA:
			return {
				...state,
				allData: {
					...state.allData,
					[action.payload.tab]: action.payload.data,
				},
			};
		case SET_ALL_DATA:
			return { ...state, allData: action.payload };
		case TOGGLE_MENU:
			return { ...state, menuOpen: !state.menuOpen };
		case CLOSE_MENU:
			return { ...state, menuOpen: false };
		case SET_DIRTY:
			return { ...state, isDirty: action.payload };
		default:
			return state;
	}
};

function SettingsPage({ menu, title }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { notifications } = useAuth();

	const [state, dispatch] = useReducer(settingsReducer, {
		activeTab: menu[0]?.tab || null,
		menuOpen: false,
		allData: {},
		isDirty: false,
	});

	const [pendingTab, setPendingTab] = useState(null);
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	useEffect(() => {
		const urlTab = new URLSearchParams(location.search).get("tab");
		if (urlTab && menu.find((item) => item.tab === urlTab)) {
			dispatch({ type: SET_ACTIVE_TAB, payload: urlTab });
		} else {
			navigate(`?tab=${menu[0]?.tab}`, { replace: true });
		}
	}, [location.search, menu, navigate]);

	useEffect(() => {
		// Carica i dati di tutte le schede una volta sola
		const fetchAllData = async () => {
			const allData = {};
			Promise.all(
				menu.map((item) => {
					if (item.getAll) {
						return item
							.getAll()
							.then((response) => {
								allData[item.tab] = response.data[item.tab];
							})
							.catch(() => {
								showErrorToast(
									`Errore durante il caricamento dei dati di ${item.label}.`
								);
							});
					}
					return null;
				})
			).then(() => dispatch({ type: SET_ALL_DATA, payload: allData }));
		};

		fetchAllData();

		// Imposta un intervallo per l'auto-update
		const intervalId = setInterval(() => {
			menu.forEach((item) => {
				if (item.autoUpdate && item.getAll)
					item.getAll()
						.then((response) => {
							dispatch({
								type: SET_DATA,
								payload: { tab: item.tab, data: response.data[item.tab] },
							});
						})
						.catch(() =>
							showErrorToast(
								`Errore durante l'aggiornamento dei dati di ${item.label}.`
							)
						);
			});
		}, UPDATE_TIME);

		// Pulizia intervallo
		return () => {
			clearInterval(intervalId);
		};
	}, [menu]);

	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (state.isDirty) {
				e.preventDefault();
				e.returnValue = "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			// dispatch({ type: SET_DIRTY, payload: false });
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [state.isDirty]);

	const changeTab = (tab) => {
		dispatch({ type: CLOSE_MENU });
		if (state.isDirty) {
			setPendingTab(tab); // Salva la scheda in sospeso
			setShowConfirmModal(true); // Mostra il modale di conferma
		} else {
			dispatch({ type: SET_ACTIVE_TAB, payload: tab });
			navigate(`?tab=${tab}`);
		}
	};

	const handleConfirmNavigation = () => {
		if (pendingTab) {
			dispatch({ type: SET_ACTIVE_TAB, payload: pendingTab });
			navigate(`?tab=${pendingTab}`);
		}
		setPendingTab(null); // Reset scheda in sospeso
		setShowConfirmModal(false); // Nascondi il modale
		dispatch({ type: SET_DIRTY, payload: false }); // Reset dirty state
	};

	const handleCancelNavigation = () => {
		setPendingTab(null); // Annulla la navigazione
		setShowConfirmModal(false); // Nascondi il modale
	};

	return (
		<PublicContext>
			<div className="flex min-h-[600px] bg-gray-100 relative">
				{/* Menu Laterale */}
				<div
					className={`menu-container lg:w-64 bg-white shadow-lg fixed lg:relative lg:top-0 top-56 left-0 z-10 h-full transition-transform duration-300 -translate-x-full  lg:translate-x-0`}
				>
					<h2 className="text-lg font-bold p-4 border-b">{title}</h2>
					<ul className="space-y-2 p-4">
						{menu.map((item) => (
							<li
								key={item.tab}
								className={`p-2 flex items-center gap-2 cursor-pointer rounded ${
									state.activeTab === item.tab
										? "bg-blue-500 text-white"
										: "text-gray-700"
								}`}
								onClick={() => {
									changeTab(item.tab);
								}}
							>
								{item.icon}
								{item.label}
								{notifications.find((n) => n.type === item.tab)?.count > 0 && (
									<span className=" bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
										{notifications.find((n) => n.type === item.tab)?.count}
									</span>
								)}
							</li>
						))}
					</ul>
				</div>

				{/* Contenuto Centrale */}
				<div className="flex-1 p-6 relative w-[100%] max-w[100%] overflow-x-auto">
					{/* Pulsante Mobile */}
					<button
						id="menu"
						aria-label="Menu"
						className={`lg:hidden absolute top-0 left-0 z-10 bg-blue-500 text-white p-2 rounded shadow-lg `}
						onClick={() => dispatch({ type: TOGGLE_MENU })}
					>
						{state.menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
					</button>

					{/* Menu Mobile */}
					<div
						className={`lg:hidden bg-white shadow-lg z-40 w-full transition-transform duration-300 transform ${
							!state.menuOpen ? "hidden" : "block"
						}`}
					>
						<ul className="space-y-2 p-4">
							{menu.map((item) => (
								<li
									key={item.tab}
									className={`p-2 flex justify-between items-center gap-2 cursor-pointer rounded ${
										state.activeTab === item.tab
											? "bg-blue-500 text-white"
											: "text-gray-700"
									}`}
									onClick={() => {
										changeTab(item.tab);
										dispatch({ type: CLOSE_MENU });
									}}
								>
									<span className="flex items-center gap-2">
										{item.icon}
										{item.label}
									</span>
									<div>
										{notifications.find((n) => n.type === item.tab)?.count >
											0 && (
											<span className=" bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
												{
													notifications.find((n) => n.type === item.tab)
														?.count
												}
											</span>
										)}
									</div>
								</li>
							))}
						</ul>
					</div>
					{/* Contenuto */}
					{menu.map(
						(item, index) =>
							state.activeTab === item.tab &&
							createElement(item.component, {
								key: index,
								state: state.allData[item.tab] || [],
								setState: (data) =>
									dispatch({
										type: SET_DATA,
										payload: { tab: item.tab, data },
									}),
								setIsDirty: (dirty) =>
									dispatch({ type: SET_DIRTY, payload: dirty }),
								...item.props,
							})
					)}
				</div>

				{/* Modale di Conferma */}
				{showConfirmModal && (
					<ConfirmModal
						message="Hai delle modifiche non salvate. Sei sicuro di voler continuare?"
						onConfirm={handleConfirmNavigation}
						onCancel={handleCancelNavigation}
					/>
				)}
			</div>
		</PublicContext>
	);
}

export default SettingsPage;
